import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FiHome, FiSettings, FiUsers, FiMenu,
  FiTruck, FiPackage, FiShoppingCart, FiFileText,
  FiCreditCard, FiShoppingBag, FiBarChart2,
  FiChevronDown, FiChevronRight, FiUser,
  FiDollarSign, FiBook, FiGrid,
  FiTag, FiTool, FiSearch, FiBell, FiHelpCircle,
  FiLogOut, FiArchive, FiArrowDownCircle,
  FiRepeat, FiShield, FiDatabase, FiGlobe
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import '../../styles/Layout.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/* ── Reloj ─────────────────────────────────────────────────────────── */
function Clock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const pad = (n) => String(n).padStart(2, '0');
  const hh = pad(time.getHours()), mm = pad(time.getMinutes()), ss = pad(time.getSeconds());
  const dd = pad(time.getDate()), mo = pad(time.getMonth() + 1), yy = time.getFullYear();
  return (
    <span className="header-clock">
      {hh}:{mm}:{ss} &nbsp; {dd}/{mo}/{yy}
    </span>
  );
}

/* ── Etiqueta de sección ────────────────────────────────────────────── */
function NavSectionLabel({ label }) {
  return <div className="nav-section-label">{label}</div>;
}

/* ── Grupo colapsable ───────────────────────────────────────────────── */
function NavGroup({ icon, label, children, defaultOpen }) {
  const location = useLocation();
  const anyActive = React.Children.toArray(children).some(
    (c) => c.props?.to && location.pathname.startsWith(c.props.to.split('/').slice(0, 2).join('/'))
  );
  const [open, setOpen] = useState(defaultOpen || anyActive);
  return (
    <div className="nav-group">
      <button
        className={`nav-item nav-group-header ${anyActive ? 'active' : ''}`}
        onClick={() => setOpen(!open)}
      >
        <span className="nav-icon">{icon}</span>
        <span className="nav-label">{label}</span>
        <span className="nav-arrow">{open ? <FiChevronDown /> : <FiChevronRight />}</span>
      </button>
      {open && <div className="nav-sub">{children}</div>}
    </div>
  );
}

/* ── Enlace de navegación ───────────────────────────────────────────── */
function NavLink({ to, label, icon, sub }) {
  const location = useLocation();
  const active = location.pathname === to || location.pathname.startsWith(to + '/');
  return (
    <Link to={to} className={`nav-item ${sub ? 'nav-sub-item' : ''} ${active ? 'active' : ''}`}>
      {icon && <span className="nav-icon">{icon}</span>}
      <span className="nav-label">{label}</span>
    </Link>
  );
}

/* ── Datos de configuración (con keys para filtrado) ────────────────── */
const CONFIG_ITEMS = [
  { key: 'config-unidades',         to: '/config/unidades',         label: 'Unidad de Medida', icon: <FiTool /> },
  { key: 'config-formas-pago',      to: '/config/formas-pago',      label: 'Forma Pago',       icon: <FiCreditCard /> },
  { key: 'config-tipos-documento',  to: '/config/tipos-documento',  label: 'Tipo Documento',   icon: <FiBook /> },
  { key: 'config-docs-pago',        to: '/config/docs-pago',        label: 'Documentos Pago',  icon: <FiFileText /> },
  { key: 'config-series-venta',     to: '/config/series-venta',     label: 'Serie Venta',      icon: <FiTag /> },
  { key: 'config-tipos-movimiento', to: '/config/tipos-movimiento', label: 'Tipo Movimiento',  icon: <FiRepeat /> },
  { key: 'config-valores',          to: '/config/valores',          label: 'Valores',          icon: <FiDatabase /> },
];

const CAJA_ITEMS = [
  { key: 'cajas',          to: '/cajas',          label: 'Gestión Cajas' },
  { key: 'caja-apertura',  to: '/caja/apertura',  label: 'Apertura / Cierre' },
  { key: 'caja-historial', to: '/caja/historial', label: 'Historial Cuadres' },
];

/* ── Layout principal ───────────────────────────────────────────────── */
function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [avatarOpen, setAvatarOpen]   = useState(false);
  const { user, logout, isSuperAdmin, menuKeys } = useAuth();
  const navigate = useNavigate();
  const displayName = user?.full_name || user?.username || '';
  const businessName = isSuperAdmin ? 'SISTEMA — SUPERADMIN' : (user?.business_name || 'TIENDA PRINCIPAL');
  const businessLogo = !isSuperAdmin && user?.business_logo
    ? `${API_URL}${user.business_logo}`
    : null;
  const initials = displayName
    .split(' ')
    .slice(0, 2)
    .map(w => w[0] || '')
    .join('')
    .toUpperCase();

  // can(key): true si no hay restricción (menuKeys===null) o el key está habilitado
  const can = (key) => !menuKeys || menuKeys.has(key);
  const canAny = (...keys) => keys.some(k => can(k));

  useEffect(() => {
    if (isSuperAdmin) {
      document.title = 'Superadmin — DevFox';
    } else if (user?.business_name) {
      document.title = user.business_name;
    }
  }, [user, isSuperAdmin]);

  const handleLogout = () => {
    setAvatarOpen(false);
    logout();
    navigate('/login');
  };

  const visibleCaja     = CAJA_ITEMS.filter(i => can(i.key));
  const visibleConfig   = CONFIG_ITEMS.filter(i => can(i.key));

  return (
    <div className="layout-wrapper" onClick={() => setAvatarOpen(false)}>
      {/* ── HEADER ──────────────────────────────────────────────────── */}
      <header className={`top-header ${isSuperAdmin ? 'header-superadmin' : ''}`}>
        <div className="header-brand">
          {isSuperAdmin ? (
            <div className="brand-devfox">
              <svg viewBox="0 0 36 36" fill="none" className="brand-devfox-icon">
                <path d="M18 2L33 10.5V25.5L18 34L3 25.5V10.5L18 2Z"
                  fill="rgba(14,165,233,0.15)" stroke="url(#dfg)" strokeWidth="1.2"/>
                <rect x="12" y="18" width="12" height="10" rx="1" fill="#0ea5e9" fillOpacity="0.85"/>
                <rect x="14" y="20" width="3" height="2.5" rx="0.4" fill="white" fillOpacity="0.8"/>
                <rect x="19" y="20" width="3" height="2.5" rx="0.4" fill="white" fillOpacity="0.8"/>
                <rect x="14" y="24" width="3" height="4" rx="0.4" fill="white"/>
                <rect x="19" y="24" width="3" height="4" rx="0.4" fill="white" fillOpacity="0.7"/>
                <rect x="12" y="12" width="2" height="5" rx="0.5" fill="#38bdf8" fillOpacity="0.9"/>
                <rect x="15.5" y="10" width="2" height="7" rx="0.5" fill="#0ea5e9"/>
                <rect x="19" y="11" width="2" height="6" rx="0.5" fill="#38bdf8" fillOpacity="0.9"/>
                <rect x="22.5" y="9" width="2" height="8" rx="0.5" fill="#0284c7"/>
                <defs>
                  <linearGradient id="dfg" x1="3" y1="2" x2="33" y2="34" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#00c6ff"/>
                    <stop offset="100%" stopColor="#6366f1" stopOpacity="0.5"/>
                  </linearGradient>
                </defs>
              </svg>
              <span className="brand-devfox-text">DevFox</span>
            </div>
          ) : businessLogo ? (
            <img src={businessLogo} alt={businessName} className="brand-logo business-logo"
              onError={e => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <span className="brand-logo-initials">
              {businessName.replace(/[^A-Za-z0-9À-ɏ]/g, ' ').trim().split(/\s+/).slice(0,2).map(w => w[0]).join('').toUpperCase() || 'SG'}
            </span>
          )}
        </div>
        <div className="header-center">
          <button className="hamburger" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <FiMenu />
          </button>
          <div className="header-search-bar">
            <FiSearch size={14} />
            <input type="text" placeholder="Buscar en el sistema..." readOnly />
          </div>
          <span className="header-business-name">{businessName}</span>
        </div>
        <div className="header-right">
          <Clock />
          <button className="header-icon-btn" title="Notificaciones"><FiBell size={15} /></button>
          <button className="header-icon-btn" title="Ayuda"><FiHelpCircle size={15} /></button>
          <span className="header-user">{displayName.toUpperCase()}</span>

          {/* Avatar con dropdown */}
          <div
            className="avatar-wrapper"
            onClick={e => { e.stopPropagation(); setAvatarOpen(o => !o); }}
          >
            <div className="header-avatar">{initials || <FiUser size={14} />}</div>

            {avatarOpen && (
              <div className="avatar-dropdown">
                <div className="avatar-dropdown-info">
                  <span className="avatar-dropdown-name">{displayName}</span>
                  <span className="avatar-dropdown-biz">{businessName}</span>
                </div>
                <div className="avatar-dropdown-divider" />
                <button className="avatar-dropdown-item avatar-logout" onClick={handleLogout}>
                  <FiLogOut size={13} />
                  Salir del sistema
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="layout-body">
        {/* ── SIDEBAR ─────────────────────────────────────────────── */}
        <aside className={`sidebar ${sidebarOpen ? '' : 'closed'}`}>
          <div className={`sidebar-agency ${isSuperAdmin ? 'sidebar-agency-superadmin' : ''}`}>
            {isSuperAdmin ? 'SUPERADMIN' : 'TIENDA PRINCIPAL'}
          </div>

          <nav className="sidebar-nav">

            {/* Superadmin: panel de empresas */}
            {isSuperAdmin && (
              <>
                <NavSectionLabel label="SUPERADMIN" />
                <NavLink to="/empresas" label="Gestión Empresas" icon={<FiGlobe />} />
              </>
            )}

            {/* ── Menú de empresa (oculto para superadmin) ── */}
            {!isSuperAdmin && (
              <>
                {can('inicio') && <NavLink to="/" label="Inicio" icon={<FiHome />} />}

                {canAny('clientes','proveedores','familia-productos','marcas','almacenes','productos') && (
                  <NavSectionLabel label="MAESTROS" />
                )}
                {can('clientes')          && <NavLink to="/clientes"         label="Clientes"          icon={<FiUsers />} />}
                {can('proveedores')       && <NavLink to="/proveedores"       label="Proveedores"       icon={<FiTruck />} />}
                {can('familia-productos') && <NavLink to="/familia-productos" label="Familia Productos" icon={<FiGrid />} />}
                {can('marcas')            && <NavLink to="/marcas"            label="Marcas"            icon={<FiTag />} />}
                {can('almacenes')         && <NavLink to="/almacenes"         label="Almacenes"         icon={<FiArchive />} />}
                {can('productos')         && <NavLink to="/productos"         label="Productos"         icon={<FiPackage />} />}

                {canAny('movimientos','transferencias','stocks') && (
                  <NavSectionLabel label="INVENTARIOS" />
                )}
                {can('movimientos')    && <NavLink to="/movimientos"    label="Movimientos"    icon={<FiRepeat />} />}
                {can('transferencias') && <NavLink to="/transferencias" label="Transferencias" icon={<FiArrowDownCircle />} />}
                {can('stocks')         && <NavLink to="/stocks"         label="Stocks"         icon={<FiDatabase />} />}

                {canAny('cotizaciones','ventas','cuentas-cobrar','cajas','caja-apertura','caja-historial') && (
                  <NavSectionLabel label="VENTAS" />
                )}
                {can('cotizaciones')  && <NavLink to="/cotizaciones"   label="Cotización"         icon={<FiFileText />} />}
                {can('ventas')        && <NavLink to="/ventas"         label="Ventas"             icon={<FiShoppingCart />} />}
                {can('cuentas-cobrar')&& <NavLink to="/cuentas-cobrar" label="Cuentas por Cobrar" icon={<FiFileText />} />}
                {visibleCaja.length > 0 && (
                  <NavGroup icon={<FiDollarSign />} label="Caja">
                    {visibleCaja.map(item => (
                      <NavLink key={item.to} to={item.to} label={item.label} sub />
                    ))}
                  </NavGroup>
                )}

                {canAny('compras','cuentas-pagar') && (
                  <NavSectionLabel label="COMPRAS" />
                )}
                {can('compras')      && <NavLink to="/compras"       label="Compras"            icon={<FiShoppingBag />} />}
                {can('cuentas-pagar')&& <NavLink to="/cuentas-pagar" label="Cuentas por Pagar"  icon={<FiCreditCard />} />}

                {can('reportes') && (
                  <>
                    <NavSectionLabel label="REPORTES" />
                    <NavLink to="/reportes" label="Reportes" icon={<FiBarChart2 />} />
                  </>
                )}

                {visibleConfig.length > 0 && (
                  <>
                    <NavSectionLabel label="CONFIGURACION" />
                    <NavGroup icon={<FiSettings />} label="Configuracion">
                      {visibleConfig.map(item => (
                        <NavLink key={item.to} to={item.to} label={item.label} icon={item.icon} sub />
                      ))}
                    </NavGroup>
                  </>
                )}

                {canAny('usuarios','perfiles') && (
                  <NavSectionLabel label="SEGURIDAD" />
                )}
                {can('usuarios') && <NavLink to="/usuarios" label="Usuarios" icon={<FiUsers />} />}
                {can('perfiles') && <NavLink to="/perfiles" label="Perfiles" icon={<FiShield />} />}
              </>
            )}

          </nav>

        </aside>

        {/* ── CONTENIDO ───────────────────────────────────────────── */}
        <main className="main-content" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/fondo_layout.png)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
          {children}
        </main>
      </div>
    </div>
  );
}

export default Layout;
